<template>
  <v-card>
    <v-card-title>
        Alarm Timeline
        <v-btn icon @click="showLegend = true"><v-icon>mdi-information</v-icon></v-btn>
        <span style="font-size: 14px;">(Press the 'L' key to toggle alarm timeline legend.)</span>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text style="height: 100%;">
        <div class="timeline-legend" v-if="showLegend">
            <div class="top">
                <span v-text="`${alarmLegends.length} Alarms`"></span>
            </div>
            <div class="alarm-key" v-for="alarmLegend in alarmLegends" :key="alarmLegend.color">
                <div class="sphere" :style="{backgroundColor: alarmLegend.color}" />
                <div class="text-container">
                    <span class="ol" v-html="`<b>${alarmLegend.alarm.alarm_office}</b>/<b>${alarmLegend.alarm.alarm_location}</b>`" />
                    <span class="desc" v-text="`${alarmLegend.alarm.alarm_description}`" />
                </div>
            </div>
            <v-btn class="close" icon @click="showLegend = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="timeline-container">
            <v-timeline class="timeline" align-top>
                <v-timeline-item v-for="(item, index) in historyObjects" :key="index" :color="item.color">
                    <template v-slot:icon>
                        <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" v-if="item.actionIcon.includes('mdi-')">{{item.actionIcon}}</v-icon>
                                <span v-on="on" style="color: #ffffff; font-weight: bold;" v-else>{{item.actionIcon}}</span>
                            </template>
                            <span>{{item.actionHoverHelp}}</span>
                        </v-tooltip>
                    </template>

                    <v-card :color="item.color" dark>
                        <v-card-title class="card-title">
                            <v-avatar class="username-avatar mr-2" v-if="item.userImage">
                                <img :src="getUserImage(item.userImage)" />
                            </v-avatar>
                            <v-icon class="mr-2" large v-else>{{item.userIcon}}</v-icon>
                            <div class="title-section">
                                <span class="time">{{item.alarm_action_timestamp}}</span>
                                <span class="username">{{item.username}}</span>
                                <v-icon class="cloud" color="#ffffff" v-if="item.fromCloud">mdi-cloud</v-icon>
                                <span class="alarm-index" v-text="`${item.historyIndex}/${item.historyTotal}`" />
                            </div>
                        </v-card-title>

                        <v-card-text class="white text--primary">
                            <span style="display: block; padding-top: 10px; color: #000000;">{{item.alarm_action_details}}</span>
                        </v-card-text>
                    </v-card>
                </v-timeline-item>
            </v-timeline>
        </div>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions>
        <div class="flex-grow-1"></div>
        <!-- <v-btn text @click="backToTop">Back to Top</v-btn> -->
        <v-btn text @click="$emit('close')">Close</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
    name: 'AlarmTimeline',
    props: ['alarms'],
    components: {

    },
    data: () => ({
        historyObjects: [],
        alarmColors: [
            '#3F51B5',
            '#66BB6A',
            '#E57373',
            '#8E25AA',
            '#E91E63',
            '#90CAF9',
            '#FF9800',
            '#C6FF00',
            '#F44336',
            '#009688',
            '#795548',
            '#757575'
        ],
        alarmLegends: [],
        showLegend: false,
        actionIconList: [
            {value: 'acked', icon: 'A', help: 'Acknowledged'},
            {value: 'unacked', icon: 'UA', help: 'Un-Acknowledged'},
            {value: 'cleared', icon: 'C', help: 'Cleared'},
            {value: 'uncleared', icon: 'UC', help: 'Un-Cleared'},
            {value: 'marked as service affecting', icon: 'SA', help: 'Marked as Service Affecting'},
            {value: 'unmarked as service affecting', icon: '!SA', help: 'Un-Marked as Service Affecting'},
            {value: 'manual entry', icon: 'mdi-bell-plus', help: 'Manually Created Alarm'},
            {value: 'escalated', icon: 'mdi-bell-alert', help: 'Alarm Escalated'},
            {value: 'paging rule', icon: 'mdi-phone-check', help: 'Paging Rule Matched'},
            {value: 'did not page', icon: 'mdi-phone-off', help: 'Did Not Page'},
        ]
    }),

    computed: {
        unmodifiedAlarm: function() {
            return `Alarm Description: ${this.$props.alarm.details.original.alarm_description} \n\nAlarm Severity: ${this.$props.alarm.details.original.alarm_severity}`;
        }
    },

    methods: {
        getUserImage: function(username) {
            if(username == this.$store.state.user.user_username) {
                return this.$store.state.userImage;
            }
            return this.$store.state.batchConfig.users.find(u => u.user_username == username).userImage;
        },

        backToTop: function() {
            let myDiv = document.getElementById('timeline-card');
            myDiv.scrollTop = 0;
        },
        
        buildTimeline: async function() {
            let timeCompare = function(a,b) {
                if (a.alarm_action_timestamp < b.alarm_action_timestamp) {
                    return -1;
                }
                if (a.alarm_action_timestamp > b.alarm_action_timestamp) {
                    return 1;
                }
                return 0;
            };

            let alarmIds = this.$props.alarms.map(x => x.alarm_id);
            TELSA.loading(true);
            let route = (TELSA.apiHost + "alarms/historyremarks");
            let historyremarks = (await this.$http.post(route, {args: {alarmIds: alarmIds}})).body;
            TELSA.loading(false);

            // Build alarm legend
            for(let [index, alarm] of this.$props.alarms.entries()) {
                let alarmColor = this.alarmColors[index];
                this.alarmLegends.push({alarm: alarm, color: alarmColor});
                historyremarks.find(hr => hr.alarm_id == alarm.alarm_id).color = alarmColor;
            };

            let historyObjects = [];
            historyObjects = historyremarks.map(x => {
                x.remarks.forEach(r => {r.current_alarm_id = x.alarm_id; r.color = x.color});
                x.history.forEach(h => {h.current_alarm_id = x.alarm_id; h.color = x.color});
                return x.remarks.concat(x.history);
            });
            historyObjects = historyObjects.reduce((acc, curr) => acc.concat(curr), []);

            historyObjects.forEach(h => {
                // user icon
                h.userIcon = 'mdi-account-circle';
                h.userImage = null;
                h.userIcon = 'mdi-account-circle';
                if(h.username == 'PAGER') {
                    h.userIcon = 'mdi-bell-ring';
                }
                else if(h.username == 'SYSTEM') {
                    h.userIcon = 'mdi-server';
                }
                else if(h.username == this.$store.state.user.user_username && this.$store.state.userImage) {
                    h.userIcon = null;
                    h.userImage = h.username;
                }
                else {
                    let userObj = this.$store.state.batchConfig.users.find(u => u.user_username == h.username);
                    if(userObj && userObj.userImage) {
                        h.userIcon = null;
                        h.userImage = h.username;
                    }
                }

                // action icon
                if(h.alarm_action == 'remark') {
                    h.actionIcon = 'mdi-message-text';
                    h.actionHoverHelp = 'Remark Added';
                }
                else if(h.alarm_action == 'paged') {
                    h.actionIcon = 'mdi-phone-ring';
                    h.actionHoverHelp = 'Paged';
                }
                else {
                    let match = this.actionIconList.find(x => x.value == h.alarm_action);
                    if(match) {
                        h.actionIcon = match.icon;
                        h.actionHoverHelp = match.help;
                    }
                    else {
                        h.actionIcon = 'mdi-pencil';
                        h.actionHoverHelp = "";
                    }
                }

                h.fromCloud = h.alarm_action_details.includes('telsasoft_cloud');
            });
            //sort
            historyObjects.sort(timeCompare);
            let total = historyObjects.length;
            for(let [i, o] of historyObjects.entries()) {
                o.historyIndex = i + 1;
                o.historyTotal = total;
            }
            historyObjects.reverse();
            this.historyObjects = historyObjects;
        },

        keyDownListener: function(keyEvent) {
            if(keyEvent.key.toLowerCase() == 'l') {
                this.showLegend = !this.showLegend;
            }
        },
    },

    mounted() {
        this.buildTimeline();
        document.addEventListener("keydown", this.keyDownListener);
    },

    destroyed() {
        document.removeEventListener("keydown", this.keyDownListener);
    }
};
</script>

<style scoped>

    .timeline-legend {
        position: fixed;
        display: block;
        width: 600px;
        height: 400px;
        border: #ffffff solid 1px;
        border-radius: 10px;
        z-index: 100;
        background-color: #303030;
    }

    .timeline-legend .top {
        border-bottom: rgba(255, 255, 255, 0.12) solid 2px;
        height: 60px;
        padding-top: 20px;
        padding-left: 20px;
    }
    .timeline-legend .top span {
        font-size: 18px;
        font-weight: bold;
    }

    .timeline-legend .alarm-key {
        padding-left: 10px;
        width: 100%;
        border-bottom: rgba(255, 255, 255, 0.12) solid 1px;
        height: 50px;
    }
    .timeline-legend .alarm-key .sphere {
        display: inline-block;
        margin-top: 10px;
        height: 30px;
        width: 30px;
        border-radius: 50%;
        border: #ffffff solid 1px;
        vertical-align: top;
    }
    .timeline-legend .alarm-key .text-container {
        display: inline-block;
        margin-left: 10px;
        width: calc(100% - 50px);
    }
    .timeline-legend .alarm-key .text-container .ol {
        display: block;
        font-size: 14px;
        width: 100%;
    }
    .timeline-legend .alarm-key .text-container .desc {
        display: block;
        font-size: 14px;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .timeline-legend .close {
        position: absolute;
        top: 10px;
        right: 10px;
    }

    

    .timeline-container {
        position: relative;
        padding: 10px;
        display: inline-block;
        width: 100%;
        /* height: calc(100vh - 260px); */
        
    }

    .timeline {
        position: relative;
        overflow-y: auto;
    }

    .card-title {
        padding: 7px;
    }

    .username-avatar {
        height: 36px !important;
        min-width: 36px !important;
        width: 36px !important;
        display: inline-block;
    }

    .title-section {
        position: relative;
        display: inline-block;
        line-height: 16px;
        margin-top: 4px;
        width: calc(100% - 45px);
    }

    .title-section .time {
        display: block;
    }
    .title-section .username {
        display: inline-block;
        font-size: 15px;
    }
    .title-section .cloud {
        display: inline-block;
        font-size: 15px;
        margin-left: 5px;
        vertical-align: bottom;
    }
    .title-section .alarm-index {
        float: right;
        font-size: 16px;
    }

</style>
